import { useRef, useState } from 'react';
import { ApiChatMessageSendStatus, type ChatDialogMessageResponse } from '@/core/generated/api';
import { useChatDialogWorkspace } from '@/entities/ChatDialog/model/workspace';
import { getErrorMessage } from '@/shared/lib/errors';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { RequestError } from '@/shared/ui/RequestState';
import classes from '../Workspace/ChatDialogPage.module.scss';

interface ChatMessageSendProblemProps {
    message: ChatDialogMessageResponse;
    dialogId: string;
    canSend: boolean;
}

/** Выделяет проблемную отправку и позволяет вручную повторить текущую попытку */
export const ChatMessageSendProblem = (
    { message, dialogId, canSend }: ChatMessageSendProblemProps
) => {
    const retryMessage = useChatDialogWorkspace((state) => state.retryMessage);
    const busyRef = useRef(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const unknown = message.sendStatus?.id === ApiChatMessageSendStatus.Unknown;

    /** Возвращает сообщение в очередь с защитой от повторного нажатия */
    const retry = async (): Promise<void> => {
        if (busyRef.current || !message.id || !message.retryAttemptId) return;
        busyRef.current = true;
        setBusy(true);
        setError(null);

        try {
            await retryMessage(dialogId, message.id, message.retryAttemptId);
        } catch (error: unknown) {
            setError(getErrorMessage(error, 'Не удалось повторить отправку'));
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
    };

    return (
        <div
            className={classes.sendProblem}
            role="alert"
        >
            <strong>
                <i
                    className="pi pi-exclamation-circle"
                    aria-hidden="true"
                />
                {unknown
                    ? 'Результат отправки не подтверждён'
                    : 'Ошибка отправки'}
            </strong>
            <p>{unknown
                ? 'Salebot мог получить сообщение. Проверьте диалог в Salebot перед повтором, чтобы избежать дубля.'
                : 'Сообщение не отправлено. Попробуйте повторить отправку.'}</p>
            {canSend && message.retryAttemptId && (
                <ActionButton
                    type="button"
                    label="Повторить отправку"
                    icon="pi pi-refresh"
                    color="danger"
                    loading={busy}
                    disabled={busy}
                    onClick={retry}
                />
            )}
            {error &&
                <RequestError
                    message={error}
                    compact
                />
            }
        </div>
    );
};
